/**
 * Client-side filtering for the collection grid.
 *
 * Designer checkboxes, a max-price slider and price sorting all act on the
 * products already on the page, so no filtering app is needed and sorting never
 * reloads away the active filters. Cards carry their designer handles and price
 * as data attributes.
 */

function handleize(str) {
  if (!str) return '';
  return str
    .toString()
    .replace(/ø/gi, 'o')
    .replace(/æ/gi, 'ae')
    .replace(/å/gi, 'a')
    .replace(/ä/gi, 'a')
    .replace(/ö/gi, 'o')
    .replace(/ü/gi, 'u')
    .replace(/ß/gi, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeDesigner(str) {
  if (!str) return '';
  return str
    .toString()
    .replace(/ø/gi, 'o')
    .replace(/æ/gi, 'ae')
    .replace(/å/gi, 'a')
    .replace(/ä/gi, 'a')
    .replace(/ö/gi, 'o')
    .replace(/ü/gi, 'u')
    .replace(/ß/gi, 'ss')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Exact designer match (normalised). Used on designer-landing pages so a single
 * designer like "George Nelson" never pulls in a collaborative entry such as
 * "Arthur Umanoff / George Nelson" (which the fuzzy matcher would, via its
 * substring rule). Handles and display names normalise to the same token, so
 * "george-nelson" and "George Nelson" still match.
 */
function matchesDesignerExact(a, b) {
  if (!a || !b) return false;
  if (a.toLowerCase().trim() === b.toLowerCase().trim()) return true;
  return normalizeDesigner(a) === normalizeDesigner(b);
}

function matchesDesigner(a, b) {
  if (!a || !b) return false;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  const na = normalizeDesigner(a);
  const nb = normalizeDesigner(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length >= 4 && nb.length >= 4 && (na.includes(nb) || nb.includes(na))) return true;
  const noVowelsA = na.replace(/[aeiouy]/g, '');
  const noVowelsB = nb.replace(/[aeiouy]/g, '');
  if (noVowelsA.length >= 3 && noVowelsA === noVowelsB) return true;
  return false;
}

class CollectionFilter extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-filter-item]'));
    this.designerInputs = Array.from(this.querySelectorAll('[data-filter-designer]'));
    this.priceInput = this.querySelector('[data-filter-price]');
    this.priceOutput = this.querySelector('[data-range-output]');
    this.priceFill = this.querySelector('[data-range-fill]');
    this.clearButtons = Array.from(this.querySelectorAll('[data-filter-clear]'));
    this.activeFilters = this.querySelector('[data-active-filters]');
    this.countBadge = this.querySelector('[data-filter-count]');
    this.empty = this.querySelector('[data-filter-empty]');
    this.pagination = this.querySelector('[data-filter-pagination]');
    this.sortInputs = Array.from(this.querySelectorAll('[data-filter-sort]'));
    this.sortLabel = this.querySelector('[data-sort-label]');
    this.grid = this.querySelector('.collection__grid');
    this.titleElement = this.querySelector('[data-collection-title]') || this.closest('.collection')?.querySelector('.collection-header__title') || document.querySelector('.collection-header__title');
    this.originalTitle = this.titleElement?.dataset?.originalTitle || this.titleElement?.textContent || '';

    /**
     * sourceDesigners — the designer(s) that were pre-selected via the URL
     * when the user arrived from the "Shop by Designer" section.
     */
    this.sourceDesigners = [];
    this.sourceDesignerNames = [];

    const initParams = new URLSearchParams(window.location.search);
    const hasDesignerParam =
      initParams.has('designer') ||
      initParams.has('filter.designer') ||
      initParams.has('filter.p.m.custom.designer');
    const fromParam = initParams.get('from');
    const isAllCollection = window.location.pathname.endsWith('/all') || window.location.pathname.includes('/collections/all');

    this.isDesignerLanding = (fromParam === 'designer' || fromParam === 'shop-by-designer') && isAllCollection;
    if (this.isDesignerLanding) {
      document.documentElement.classList.add('is-designer-landing');
    }

    this.apply = this.apply.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.designerInputs.forEach((input) => {
      input.addEventListener('click', (e) => {
        if (this.isDesignerLanding && !input.checked) {
          e.preventDefault();
          input.checked = true;
        }
      });
      input.addEventListener('change', () => {
        if (this.isDesignerLanding) {
          input.checked = true;
          return;
        }
        this.sourceDesigners = [];
        this.sourceDesignerNames = [];
        this.fallbackDesigners = [];
        this.clientPage = 1;
        this.syncUrl();
        this.apply();
      });
    });
    this.priceInput?.addEventListener('input', () => {
      this.clientPage = 1;
      this.apply();
    });
    this.sortInputs.forEach((input) => input.addEventListener('change', () => this.sort()));
    this.clearButtons.forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.clear();
      })
    );

    this.activeFilters?.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-remove-designer]');
      if (!chip) return;
      event.preventDefault();
      event.stopPropagation();
      this.removeDesigner(chip.dataset.removeDesigner);
    });

    this.toolbar = this.querySelector('.collection__toolbar');
    if (this.toolbar) {
      this.toolbar.addEventListener('click', (event) => {
        if (!event.target.closest('.collection__filters-button')) {
          event.preventDefault();
        }
      });
    }

    window.addEventListener('popstate', this.onPopState);

    // Close the sort dropdown when clicking anywhere outside it.
    this.sortDetails = this.querySelector('.facets__sort');
    if (this.sortDetails) {
      this.onDocClick = (event) => {
        if (this.sortDetails.open && !this.sortDetails.contains(event.target)) {
          this.sortDetails.removeAttribute('open');
        }
      };
      document.addEventListener('click', this.onDocClick);
    }

    this.readUrlParams();
    // Apply the default (or URL-selected) sort order on load. sort() both sinks
    // sold pieces last and orders by price, so the rendered grid matches the
    // checked "Sort by" radio/label instead of the raw collection.products order
    // coming from Liquid (which follows the collection's admin sort).
    this.sort();
    this.apply();
    this.revealCheckedDesigner();

    // Load the rest of the collection in the background so sold products can be
    // ordered last across the WHOLE collection (not just the current page) and
    // pagination becomes client-side. See setupGlobalSoldLast().
    this.setupGlobalSoldLast();
  }

  /**
   * Number of products shown per client-side page once the whole collection
   * has been loaded into the DOM.
   */
  get CLIENT_PAGE_SIZE() {
    return 48;
  }

  /**
   * On a normal (unfiltered) collection page, fetch every remaining page of the
   * collection, append the cards, then sink sold products to the very bottom
   * across the whole collection and switch to client-side pagination.
   *
   * Skipped for designer-landing pages and whenever a designer/price filter is
   * already active — those flows own the grid and fetch their own products.
   */
  async setupGlobalSoldLast() {
    if (this.isDesignerLanding) return;

    const params = new URLSearchParams(window.location.search);
    const hasFilterParam =
      params.has('designer') ||
      params.has('filter.designer') ||
      params.has('filter.p.m.custom.designer');
    if (hasFilterParam) return;
    if (this.selectedDesigners.length > 0) return;

    // Work out how many server pages exist from the rendered pagination nav.
    let lastPage = 1;
    if (this.pagination) {
      this.pagination.querySelectorAll('a, span').forEach((el) => {
        const n = parseInt(el.textContent.trim(), 10);
        if (!Number.isNaN(n)) lastPage = Math.max(lastPage, n);
      });
    }

    this.clientPage = 1;

    if (lastPage <= 1) {
      // Single server page: everything is already here, so order it by the
      // active sort (price, sold last) then paginate it on the client.
      this.allLoaded = true;
      this.sort();
      return;
    }

    // Paginate the first page immediately so the shopper sees a tidy first page
    // (not one long unsorted list) while the rest of the collection loads. The
    // pager itself is held back until everything is loaded and sorted, so it
    // appears once, with the correct final page count.
    this.loadingAll = true;
    this.allLoaded = true;
    this.apply();

    this.toggleBackgroundLoader(true);
    await this.loadRemainingPages(lastPage);
    this.loadingAll = false;
    this.toggleBackgroundLoader(false);
    this.apply();
  }

  /**
   * Shows/hides a compact "loading more pieces" indicator during the background
   * load. Kept small and placed under the grid so it never pushes the products
   * that are already on screen.
   */
  toggleBackgroundLoader(show) {
    if (!this.bgLoader) {
      this.bgLoader = document.createElement('div');
      this.bgLoader.className = 'collection__loading-more';
      this.bgLoader.setAttribute('role', 'status');
      this.bgLoader.innerHTML =
        '<span class="collection__loading-more-spinner" aria-hidden="true"></span>' +
        '<span class="collection__loading-more-text">Loading more pieces&hellip;</span>';
      this.grid.insertAdjacentElement('afterend', this.bgLoader);
    }
    this.bgLoader.hidden = !show;
  }

  async loadRemainingPages(lastPage) {
    const base = window.location.pathname;
    const sectionId =
      this.closest('.shopify-section')?.id?.replace('shopify-section-', '') || '';

    const existingLinks = new Set(
      Array.from(this.grid.querySelectorAll('.product-card__link')).map((a) =>
        a.getAttribute('href')
      )
    );

    for (let page = 2; page <= lastPage; page++) {
      try {
        const url = sectionId
          ? `${base}?section_id=${encodeURIComponent(sectionId)}&page=${page}`
          : `${base}?page=${page}`;
        const res = await fetch(url);
        if (!res.ok) continue;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetched = Array.from(doc.querySelectorAll('.collection__grid-item'));
        fetched.forEach((item) => {
          // Skip the hidden metaobject designer extras; they are page-1 only.
          if (item.dataset.metaobjectProduct === 'true') return;
          const href = item.querySelector('.product-card__link')?.getAttribute('href');
          if (href && !existingLinks.has(href)) {
            existingLinks.add(href);
            // Append hidden so the grid doesn't balloon while loading; the final
            // apply() below reveals the correct client page once sorted.
            item.hidden = true;
            this.grid.appendChild(item);
          }
        });
      } catch (err) {
        console.warn('Failed to load collection page', page, err);
      }
    }

    // Everything is now in the DOM: refresh the item list, order the full set by
    // the active sort (price, sold last), retire the server pagination and switch
    // to client paging.
    this.items = Array.from(this.querySelectorAll('[data-filter-item]'));
    this.allLoaded = true;
    this.sort();
    if (this.pagination) {
      this.pagination.hidden = true;
      this.pagination.style.display = 'none';
    }
    this.clientPage = 1;
    this.apply();
  }

  /**
   * Once the whole collection is loaded, limit the visible (filter-passing)
   * cards to the current client page and render the pager. Called at the end of
   * apply(), after filters have set each item's hidden state.
   */
  applyClientPagination() {
    if (!this.allLoaded || this.isDesignerLanding) return;

    // Follow DOM order (sinkSold/sort reorder the grid, not the items array).
    const visibleItems = Array.from(
      this.grid.querySelectorAll('[data-filter-item]')
    ).filter((item) => !item.hidden);
    const pageSize = this.CLIENT_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(visibleItems.length / pageSize));

    if (this.clientPage > totalPages) this.clientPage = totalPages;
    if (this.clientPage < 1) this.clientPage = 1;

    const start = (this.clientPage - 1) * pageSize;
    const end = start + pageSize;
    visibleItems.forEach((item, index) => {
      if (index < start || index >= end) item.hidden = true;
    });

    this.renderClientPager(totalPages);
  }

  renderClientPager(totalPages) {
    // While the whole collection is still loading, keep the pager hidden and let
    // the "loading more" indicator stand in for it.
    if (this.loadingAll) {
      if (this.clientPager) {
        this.clientPager.hidden = true;
        this.clientPager.innerHTML = '';
      }
      return;
    }

    if (!this.clientPager) {
      this.clientPager = document.createElement('nav');
      this.clientPager.className = 'collection__pagination';
      this.clientPager.setAttribute('data-client-pagination', '');
      this.grid.insertAdjacentElement('afterend', this.clientPager);
      this.clientPager.addEventListener('click', (event) => {
        const target = event.target.closest('[data-page]');
        if (!target) return;
        event.preventDefault();
        const page = Number(target.dataset.page);
        if (!page || page === this.clientPage) return;
        this.clientPage = page;
        this.apply();
        const top = this.grid.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }

    if (totalPages <= 1) {
      this.clientPager.hidden = true;
      this.clientPager.innerHTML = '';
      return;
    }
    this.clientPager.hidden = false;

    const current = this.clientPage;
    const pages = [];
    const add = (n) => pages.push(n);
    const window_ = 1; // neighbours shown on each side of the current page.

    add(1);
    for (let n = current - window_; n <= current + window_; n++) {
      if (n > 1 && n < totalPages) add(n);
    }
    if (totalPages > 1) add(totalPages);
    const unique = [...new Set(pages)].sort((a, b) => a - b);

    let html = '';
    if (current > 1) {
      html += `<a href="#" class="collection__pagination-prev" data-page="${current - 1}" aria-label="Previous page">&lsaquo;</a>`;
    }
    let prev = 0;
    unique.forEach((n) => {
      if (prev && n - prev > 1) html += `<span class="deco">&hellip;</span>`;
      html +=
        n === current
          ? `<span class="current" aria-current="page">${n}</span>`
          : `<a href="#" data-page="${n}">${n}</a>`;
      prev = n;
    });
    if (current < totalPages) {
      html += `<a href="#" class="collection__pagination-next" data-page="${current + 1}" aria-label="Next page">&rsaquo;</a>`;
    }
    this.clientPager.innerHTML = html;
  }

  /**
   * The designer list scrolls, so a designer pre-selected from the URL can sit
   * below the fold of its own list. Bring the first checked one into view.
   */
  revealCheckedDesigner() {
    const checked = this.designerInputs.find((input) => input.checked);
    const list = checked?.closest('[data-designer-list]');
    const item = checked?.closest('[data-designer-item]');
    if (!list || !item) return;
    if (list.scrollHeight <= list.clientHeight) return;
    list.scrollTop = Math.max(
      0,
      item.offsetTop - list.offsetTop - list.clientHeight / 2 + item.offsetHeight / 2
    );
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
  }

  onPopState() {
    this.fallbackDesigners = [];
    this.sourceDesigners = [];
    this.sourceDesignerNames = [];
    const params = new URLSearchParams(window.location.search);
    const fromParam = params.get('from');
    const isAllCollection = window.location.pathname.endsWith('/all') || window.location.pathname.includes('/collections/all');
    this.isDesignerLanding = (fromParam === 'designer' || fromParam === 'shop-by-designer') && isAllCollection;
    if (this.isDesignerLanding) {
      document.documentElement.classList.add('is-designer-landing');
    } else {
      document.documentElement.classList.remove('is-designer-landing');
      this.querySelectorAll('[data-designer-item]').forEach((item) => {
        item.hidden = false;
        item.style.display = '';
        item.removeAttribute('data-hidden-by-landing');
      });
    }
    this.designerInputs.forEach((input) => {
      input.checked = false;
    });
    this.readUrlParams();
    this.apply();
    this.revealCheckedDesigner();
  }

  readUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const rawValues = [
      ...params.getAll('designer'),
      ...params.getAll('filter.designer'),
      ...params.getAll('filter.p.m.custom.designer'),
    ];

    if (rawValues.length === 0) return;

    const designerTargets = [];
    rawValues.forEach((val) => {
      val.split(',').forEach((v) => {
        const clean = v.trim();
        if (clean) designerTargets.push(clean);
      });
    });

    if (designerTargets.length === 0) return;

    const targetHandles = designerTargets.map(handleize);
    this.sourceDesigners = targetHandles;
    this.sourceDesignerNames = designerTargets;

    let matchedAny = false;

    if (this.isDesignerLanding && designerTargets.length > 0) {
      const designerItems = Array.from(this.querySelectorAll('[data-designer-item]'));
      designerItems.forEach((item) => {
        const input = item.querySelector('input[data-filter-designer]');
        const val = input ? input.value : '';
        const label = item.dataset.designerLabel || '';
        const labelText = item.querySelector('[data-designer-label-text]')?.textContent?.trim() || '';

        const matches = designerTargets.some((target) => {
          return (
            matchesDesignerExact(val, target) ||
            matchesDesignerExact(label, target) ||
            matchesDesignerExact(labelText, target)
          );
        });

        if (matches) {
          item.hidden = false;
          item.style.display = '';
          item.removeAttribute('data-hidden-by-landing');
          if (input) input.checked = true;
          matchedAny = true;
        } else {
          item.hidden = true;
          item.style.display = 'none';
          item.setAttribute('data-hidden-by-landing', 'true');
          if (input) input.checked = false;
        }
      });

      if (!matchedAny && designerTargets[0]) {
        const list = this.querySelector('[data-designer-list]');
        if (list) {
          const designerName = designerTargets[0];
          const li = document.createElement('li');
          li.className = 'facets__item';
          li.setAttribute('data-designer-item', '');
          li.setAttribute('data-designer-label', designerName);
          li.innerHTML = `
            <label class="facets__checkbox">
              <input type="checkbox" value="${handleize(designerName)}" data-filter-designer checked>
              <span class="facets__checkbox-label" data-designer-label-text>${designerName}</span>
            </label>
          `;
          list.prepend(li);
          this.designerInputs = Array.from(this.querySelectorAll('[data-filter-designer]'));
          matchedAny = true;
        }
      }

      const searchField = this.querySelector('[data-designer-search-field]');
      const searchOpen = this.querySelector('[data-designer-search-open]');
      const searchClose = this.querySelector('[data-designer-search-close]');
      if (searchField) { searchField.hidden = true; searchField.style.display = 'none'; }
      if (searchOpen) { searchOpen.hidden = true; searchOpen.style.display = 'none'; }
      if (searchClose) { searchClose.hidden = true; searchClose.style.display = 'none'; }
    } else {
      this.designerInputs.forEach((input) => {
        const val = input.value;
        const label = input.closest('[data-designer-item]')?.dataset.designerLabel || '';
        const labelText = input.closest('label')?.querySelector('[data-designer-label-text]')?.textContent?.trim() || '';

        const matches = designerTargets.some((target) => {
          return (
            matchesDesigner(val, target) ||
            matchesDesigner(label, target) ||
            matchesDesigner(labelText, target)
          );
        });

        if (matches) {
          input.checked = true;
          matchedAny = true;
        }
      });
    }

    // Only on desktop, where the facets are a sidebar. On a phone the panel is
    // in the flow and stays closed until the shopper asks for it.
    if (matchedAny && window.matchMedia('(min-width: 990px)').matches) {
      const filterDetails = this.closest('.collection')?.querySelector('.collection__filters') || this.querySelector('.collection__filters');
      if (filterDetails) {
        filterDetails.setAttribute('open', '');
      }
    } else if (!matchedAny) {
      this.fallbackDesigners = targetHandles;
    }

    if (designerTargets.length > 0) {
      this.fetchDesignerProducts(designerTargets[0]);
    }
  }

  syncUrl() {
    try {
      const url = new URL(window.location.href);
      const designers = this.designerInputs.filter((input) => input.checked).map((input) => input.value);
      if (designers.length > 0) {
        url.searchParams.set('designer', designers.join(','));
        url.searchParams.delete('filter.designer');
        url.searchParams.delete('filter.p.m.custom.designer');
        if (this.isDesignerLanding) {
          url.searchParams.set('from', 'designer');
        }
      } else {
        url.searchParams.delete('designer');
        url.searchParams.delete('filter.designer');
        url.searchParams.delete('filter.p.m.custom.designer');
        url.searchParams.delete('from');
      }
      window.history.replaceState({}, '', url.toString());
    } catch (_) {
      // Ignore if URL API restricted
    }
  }

  /** A card is sold when its product card renders the "Sold" badge. */
  isSold(item) {
    return !!(item.querySelector && item.querySelector('.product-card__sold-out'));
  }

  /**
   * Pushes sold cards to the end of the grid while keeping the existing order
   * among them (Array.prototype.sort is stable). Used on load so sold pieces
   * never lead the listing.
   */
  sinkSold() {
    if (!this.grid) return;
    this.items
      .slice()
      .sort((a, b) => (this.isSold(a) ? 1 : 0) - (this.isSold(b) ? 1 : 0))
      .forEach((item) => this.grid.append(item));
  }

  /** Reorders the cards in place, so the current filters stay applied. */
  sort() {
    const checked = this.sortInputs.find((input) => input.checked);
    if (!checked || !this.grid) return;

    const direction = checked.value === 'price-descending' ? -1 : 1;

    this.items
      .slice()
      .sort((a, b) => {
        // Sold pieces always sink below available ones, whatever the price order.
        const soldDiff = (this.isSold(a) ? 1 : 0) - (this.isSold(b) ? 1 : 0);
        if (soldDiff !== 0) return soldDiff;
        return (Number(a.dataset.price) - Number(b.dataset.price)) * direction;
      })
      .forEach((item) => this.grid.append(item));

    if (this.sortLabel) {
      this.sortLabel.textContent =
        checked.closest('.facets__sort-option')?.querySelector('.facets__sort-option-label')
          ?.textContent ?? '';
    }

    this.closest('.collection')?.querySelector('.facets__sort')?.removeAttribute('open');

    // Re-slice to the first client page in the new order.
    if (this.allLoaded) {
      this.clientPage = 1;
      this.apply();
    }
  }

  designerLabel(input) {
    return (
      input.closest('[data-designer-item]')?.dataset.designerLabel ||
      input.closest('label')?.querySelector('[data-designer-label-text]')?.textContent?.trim() ||
      input.value
    );
  }

  /** A removable chip beside "Clear filters" for each designer in play. */
  renderActiveFilters() {
    if (!this.activeFilters) return;

    // The landing flow pins its designer in place, so there is nothing to remove.
    if (this.isDesignerLanding) {
      this.activeFilters.replaceChildren();
      this.activeFilters.hidden = true;
      return;
    }

    const chips = this.designerInputs
      .filter((input) => input.checked)
      .map((input) => ({ value: input.value, label: this.designerLabel(input) }));

    // A designer named in the URL that matched no checkbox still filters the
    // grid, so it gets a chip of its own.
    if (chips.length === 0) {
      (this.fallbackDesigners || []).forEach((handle) =>
        chips.push({ value: handle, label: handle.replace(/-/g, ' ') })
      );
    }

    const removeLabel = this.activeFilters.dataset.removeLabel || 'Remove';

    this.activeFilters.replaceChildren(
      ...chips.map(({ value, label }) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'collection__active-filter';
        chip.dataset.removeDesigner = value;
        chip.setAttribute('aria-label', `${removeLabel} ${label}`);

        const text = document.createElement('span');
        text.className = 'collection__active-filter-label';
        text.textContent = label;

        const cross = document.createElement('span');
        cross.className = 'collection__active-filter-remove';
        cross.setAttribute('aria-hidden', 'true');
        cross.textContent = '\u00d7';

        chip.append(text, cross);
        return chip;
      })
    );

    this.activeFilters.hidden = chips.length === 0;
  }

  /** Drops one designer, leaving the other filters as they are. */
  removeDesigner(value) {
    this.clientPage = 1;
    this.designerInputs.forEach((input) => {
      if (input.value === value) input.checked = false;
    });

    if (this.fallbackDesigners?.length) {
      this.fallbackDesigners = this.fallbackDesigners.filter((handle) => handle !== value);
    }

    this.sourceDesigners = [];
    this.sourceDesignerNames = [];
    this.syncUrl();
    this.apply();
  }

  get selectedDesigners() {
    const checked = this.designerInputs.filter((input) => input.checked).map((input) => input.value);
    if (checked.length > 0) return checked;
    if (this.fallbackDesigners && this.fallbackDesigners.length > 0) {
      return this.fallbackDesigners;
    }
    return [];
  }

  get maxPrice() {
    return this.priceInput ? Number(this.priceInput.value) : Infinity;
  }

  apply() {
    const designers = this.selectedDesigners;
    const max = this.maxPrice;
    let visible = 0;

    this.items.forEach((item) => {
      const itemDesigners = (item.dataset.designer || '')
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean);
      const cardDesigner =
        item.querySelector('.product-card__designer')?.textContent?.trim() ||
        item.dataset.designerName ||
        item.querySelector('.product-card')?.dataset.designerName ||
        '';
      const cardTitle = item.querySelector('.product-card__title')?.textContent?.trim() || '';
      const isMetaobjectExtra = item.dataset.metaobjectProduct === 'true';
      const isFetched = item.dataset.fetchedProduct === 'true';
      const price = Number(item.dataset.price || 0);

      // On a designer-landing page match exactly, so "George Nelson" does not
      // also pull in "Arthur Umanoff / George Nelson". Elsewhere stay fuzzy.
      const designerMatch2 = this.isDesignerLanding ? matchesDesignerExact : matchesDesigner;
      const designerMatchesSelected =
        designers.length > 0 &&
        designers.some((handle) => {
          return (
            (cardDesigner && designerMatch2(cardDesigner, handle)) ||
            itemDesigners.some((d) => designerMatch2(d, handle)) ||
            (cardTitle && designerMatch2(cardTitle, handle))
          );
        });

      const designerMatch =
        (designers.length === 0 && !isMetaobjectExtra && !isFetched) ||
        designerMatchesSelected;
      const priceMatch = price <= max;
      const shown = designerMatch && priceMatch;

      item.hidden = !shown;
      if (shown) visible += 1;
    });

    if (this.empty) {
      this.empty.hidden = visible > 0 || Boolean(this.fetchingDesigner);
    }

    const activeCount = designers.length + (max < this.ceiling ? 1 : 0);

    if (this.pagination) {
      // Server pagination is retired once the whole collection is client-side.
      this.pagination.hidden = this.allLoaded || activeCount > 0;
    }

    this.clearButtons.forEach((button) => {
      // The one inside the empty state is part of that block's own visibility.
      if (button.closest('[data-filter-empty]')) return;
      button.hidden = activeCount === 0;
      button.style.display = activeCount === 0 ? 'none' : '';
    });

    // Once the whole collection is loaded, paginate on the client so sold
    // products stay last across every page.
    this.applyClientPagination();

    this.renderActiveFilters();

    if (this.countBadge) {
      this.countBadge.hidden = activeCount === 0;
      this.countBadge.style.display = activeCount === 0 ? 'none' : '';
      this.countBadge.textContent = activeCount;
    }

    if (this.titleElement) {
      if (this.isDesignerLanding) {
        // Landing always comes from one designer in the URL, so show that name
        // even when fuzzy matching ticked more than one checkbox (e.g. a piece
        // credited to "Arthur Umanoff / George Nelson" also matches).
        let activeLabel = '';
        if (this.sourceDesignerNames && this.sourceDesignerNames.length >= 1) {
          activeLabel = this.sourceDesignerNames[0];
        }
        if (!activeLabel) {
          const params = new URLSearchParams(window.location.search);
          activeLabel = params.get('designer') || '';
        }
        if (!activeLabel) {
          const checkedInput = this.designerInputs.find((input) => input.checked);
          if (checkedInput) {
            activeLabel =
              checkedInput.closest('[data-designer-item]')?.dataset.designerLabel ||
              checkedInput.closest('label')?.querySelector('[data-designer-label-text]')?.textContent?.trim() ||
              checkedInput.closest('label')?.textContent?.trim() ||
              '';
          }
        }
        if (activeLabel) {
          this.titleElement.textContent = activeLabel;
        }
      } else if (this.originalTitle) {
        this.titleElement.textContent = this.originalTitle;
      }
    }

    this.paintRange();

    // Auto-fetch products if zero were found on the current page for this designer (only in designer landing mode)
    if (this.isDesignerLanding && designers.length > 0 && visible === 0 && !this.fetchingDesigner) {
      const designerQuery =
        (this.sourceDesignerNames && this.sourceDesignerNames[0]) ||
        this.designerInputs.find((i) => i.checked)?.closest('[data-designer-item]')?.dataset.designerLabel ||
        this.designerInputs.find((i) => i.checked)?.closest('label')?.textContent?.trim() ||
        designers[0];
      if (designerQuery) {
        this.fetchDesignerProducts(designerQuery);
      }
    }
  }

  async fetchDesignerProducts(designerName) {
    if (!designerName || this.fetchingDesigner === designerName) return;
    this.fetchingDesigner = designerName;

    const loader = this.querySelector('[data-collection-loader]');
    if (this.isDesignerLanding) {
      if (loader) {
        loader.hidden = false;
        loader.style.display = 'flex';
      }
      document.documentElement.classList.add('is-loading-designer');
    }

    if (this.empty) this.empty.hidden = true;

    try {
      let doc = await this.querySearchProducts(designerName);
      let fetchedItems = doc ? Array.from(doc.querySelectorAll('.collection__grid-item')) : [];

      const normalizedName = designerName.replace(/ø/gi, 'o').replace(/æ/gi, 'ae').replace(/å/gi, 'a');
      if (fetchedItems.length === 0 && normalizedName !== designerName) {
        doc = await this.querySearchProducts(normalizedName);
        if (doc) {
          fetchedItems = Array.from(doc.querySelectorAll('.collection__grid-item'));
        }
      }

      if (fetchedItems.length > 0 && this.grid) {
        const existingLinks = new Set(
          Array.from(this.grid.querySelectorAll('.product-card__link')).map((a) => a.getAttribute('href'))
        );

        let addedCount = 0;
        fetchedItems.forEach((item) => {
          const href = item.querySelector('.product-card__link')?.getAttribute('href');
          if (href && !existingLinks.has(href)) {
            existingLinks.add(href);
            item.dataset.fetchedProduct = 'true';
            this.grid.appendChild(item);
            addedCount++;
          }
        });

        this.items = Array.from(this.querySelectorAll('[data-filter-item]'));
        // Order the whole designer set by the active sort (price, sold last),
        // including the products just fetched and appended.
        this.sort();
        this.apply();
      } else {
        this.sort();
        this.apply();
        if (this.empty) this.empty.hidden = this.items.filter((i) => !i.hidden).length > 0;
      }
    } catch (err) {
      console.warn('Failed to fetch designer products:', err);
    } finally {
      this.fetchingDesigner = null;
      if (loader) {
        loader.hidden = true;
        loader.style.display = 'none';
      }
      document.documentElement.classList.remove('is-loading-designer');
      if (this.grid) {
        this.grid.style.opacity = '';
        this.grid.style.pointerEvents = '';
      }
    }
  }

  async querySearchProducts(query) {
    try {
      const res = await fetch(`/search?type=product&q=${encodeURIComponent(query)}`);
      if (!res.ok) return null;
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (_) {
      return null;
    }
  }

  get ceiling() {
    return Number(this.priceInput?.max) || Infinity;
  }

  paintRange() {
    if (!this.priceInput) return;

    const ceiling = this.ceiling;
    const value = this.maxPrice;

    if (this.priceFill) {
      this.priceFill.style.setProperty('--range-start', '0%');
      this.priceFill.style.setProperty('--range-end', `${(value / ceiling) * 100}%`);
    }

    if (this.priceOutput) this.priceOutput.textContent = this.format(value);
  }

  format(value) {
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return value;
    }
  }

  clear() {
    this.clientPage = 1;
    if (this.isDesignerLanding) {
      // In designer landing mode, clear ONLY extra in-page filters (e.g. price slider, sorting),
      // NEVER remove the designer or dump all collection products!
      if (this.priceInput) {
        this.priceInput.value = this.priceInput.max;
        this.paintRange();
      }

      const defaultSort = this.sortInputs.find((input) => input.defaultChecked) || this.sortInputs[0];
      if (defaultSort) {
        defaultSort.checked = true;
        this.sort();
      }

      // Keep target designer checked!
      const targetDesigner = (this.sourceDesignerNames && this.sourceDesignerNames[0]) || '';
      this.designerInputs.forEach((input) => {
        const val = input.value;
        const label = input.closest('[data-designer-item]')?.dataset.designerLabel || '';
        const labelText = input.closest('label')?.querySelector('[data-designer-label-text]')?.textContent?.trim() || '';
        const matches = targetDesigner && (
          matchesDesignerExact(val, targetDesigner) ||
          matchesDesignerExact(label, targetDesigner) ||
          matchesDesignerExact(labelText, targetDesigner)
        );
        input.checked = Boolean(matches);
      });

      this.syncUrl();
      this.apply();
      return;
    }

    // Default flow for normal collection pages (unaffected):
    this.sourceDesigners = [];
    this.sourceDesignerNames = [];
    this.fallbackDesigners = [];
    if (this.priceInput) {
      this.priceInput.value = this.priceInput.max;
      this.paintRange();
    }
    this.designerInputs.forEach((input) => {
      input.checked = false;
    });
    this.querySelectorAll('[data-fetched-product]').forEach((el) => el.remove());
    this.items = Array.from(this.querySelectorAll('[data-filter-item]'));
    this.syncUrl();
    this.apply();
  }
}

if (!customElements.get('collection-filter')) {
  customElements.define('collection-filter', CollectionFilter);
}
